import React, { ReactNode, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import styled from 'astroturf/react';
import {
  Popover,
  PopoverProps,
  PopoverMode,
  PopoverWidthMode,
} from 'js/design_system/Popover';
import { Icon } from 'js/design_system/Icon';
import { useRandomId } from 'js/utils/hooks/useRandomId';
import { Errors } from 'js/design_system/forms/Errors';

import 'app/assets/stylesheets/design_system/form.scss';
import _DropdownMenuStyleContext from "Dropdown-DropdownMenuStyleContext.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/forms/Dropdown.tsx?styleId=DropdownMenuStyleContext";


// Because the Popover uses React.createPortal, we need to use this to ensure that
// DS2.5 styles are applied to the menu.
// Note: If we ever implement theme toggling, we'll need to either manually update
// this specific instance, or use some JS to make sure the menu respects the tokens
// from its parent's context. This current solution only works because we are assuming
// that the Dropdown component is only ever DS2.5-styled.
const DropdownMenuStyleContext = /*#__PURE__*/styled("div", null, {
  displayName: "DropdownMenuStyleContext",
  styles: _DropdownMenuStyleContext
});

export type DropdownProps<T> = {
  /** Optional value to show as selected */
  value?: T;
  /** Options to show in the dropdown */
  options?: T[];
  /** The input's label. Shown at the top of the component. */
  label?: ReactNode;
  /** The input's description. Shown directly underneath the label. */
  description?: ReactNode;
  /** The placeholder for the underlying input */
  placeholder?: string;
  /** Content to prepend before the actual text input */
  prepend?: ReactNode;
  /** Content to append after the actual text input */
  append?: ReactNode;
  /** The errors to show beneath the input */
  errors?: ReactNode[];
  /** Whether to decorate the input with error styling */
  hasError?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to allow propagation of the click event to the parent */
  allowPropagation?: boolean;
  /** Get a key from a given option (for React) */
  keyExtractor?: (option: T, index: number) => React.Key;
  /** Render callback for a single option in the typeahead dropdpown */
  renderOption: (option: T, index: number) => ReactNode;
  /** Callback to invoke when selecting an option */
  onChange: (option: T) => void;
  /** Props for the internal popover element */
  popoverProps?: Partial<PopoverProps>;
  /** Render callback for the value */
  renderValue?: (value: T) => string;
  /** Custom class name to apply to the dropdown container */
  className?: string;
  /** Custom style to apply to the dropdown container */
  style?: React.CSSProperties;
};
/**
 * # Dropdown
 * Dropdown menu of selectable options. Supports labels, descriptions, and error messages.
 *
 * Props:
 * - `value`: Optional value to show as selected
 * - `options`: Options to show in the dropdown
 * - `label`: The input's label. Shown at the top of the component.
 * - `description`: The input's description. Shown directly underneath the label.
 * - `placeholder`: The placeholder for the underlying input
 * - `prepend`: Content to prepend before the actual text input (e.g. an icon)
 * - `append`: Content to append after the actual text input
 * - `errors`: The errors to show beneath the input
 * - `hasError`: Whether to decorate the input with error styling
 * - `disabled`: Whether the input is disabled
 * - `keyExtractor`: Get a key from a given option (for React)
 * - `renderOption`: Render callback for how to display a single option in the dropdown
 * - `onChange`: Callback to invoke when selecting an option
 * - `popoverProps`: Props for the internal popover element
 * - `renderValue`: Render callback for the value
 * - `className`: Custom class name to apply to the dropdown container
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Dropdown } from 'js/design_system/forms/Dropdown';
 *
 * const [value, setValue] = useState('');
 *
 *  <Dropdown
 *    label="Month"
 *    description="Choose a month!"
 *    value={value}
 *    options={OPTIONS}
 *    renderOption={(option: string) => option}
 *    onChange={(string) => setValue(string)}
 *  />
 *
 * ```
 */
export const Dropdown = <T,>({
  value,
  options = [],
  label,
  description,
  placeholder,
  prepend,
  append,
  errors,
  hasError,
  disabled,
  allowPropagation = false,
  keyExtractor = (option) => JSON.stringify(option),
  renderOption,
  onChange,
  popoverProps = {},
  className,
  style,
  renderValue = String,
}: DropdownProps<T>) => {
  // Random ID for ARIA attributes
  const id = useRandomId();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const inputContainerClasses = classNames(className, 'ds2-5-input--container');

  const dropdownButtonClasses = classNames('ds-dropdown-button', {
    'ds2-5-input--error': !disabled && hasError,
  });

  const showPlaceholder = value === undefined;
  const buttonContentClasses = classNames('ds-dropdown-button--contents', {
    'ds2-5-input--placeholder': showPlaceholder,
  });

  const labelClasses = label
    ? classNames('ds2-5-form--label', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const descriptionClasses = description
    ? classNames('ds2-5-form--desc', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const handleButtonClick = useCallback(
    (event: React.MouseEvent) => {
      if (!allowPropagation) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!disabled) {
        setOpen((prev) => !prev);
      }
    },
    [disabled, allowPropagation],
  );

  return (
    <div
      className={inputContainerClasses}
      style={style}
      aria-disabled={disabled}
    >
      {label && (
        <label
          htmlFor={`${id}-input`}
          className={labelClasses}
          id={`${id}-label`}
        >
          {label}
        </label>
      )}
      {description && (
        <div className={descriptionClasses} id={`${id}-desc`}>
          {description}
        </div>
      )}

      <Popover
        popoverRef={popoverRef}
        open={open}
        offset={4}
        mode={PopoverMode.Click}
        widthMode={PopoverWidthMode.Activator}
        closeOnClick={false}
        maxHeight={288}
        render={() => (
          <DropdownMenuStyleContext className="ds2-5-dropdown-menu">
            {options.map((option, index) => (
              <button
                className="ds2-5-dropdown-item"
                key={keyExtractor(option, index)}
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(option);
                  setOpen(false);
                }}
              >
                {renderOption(option, index)}
              </button>
            ))}
          </DropdownMenuStyleContext>
        )}
        onChange={!disabled ? setOpen : undefined}
        {...popoverProps}
      >
        <button
          ref={buttonRef}
          className={dropdownButtonClasses}
          disabled={disabled}
          onClick={handleButtonClick}
          type="button"
        >
          {prepend}
          <div className={buttonContentClasses}>
            {showPlaceholder ? (placeholder ?? '') : renderValue(value)}
          </div>
          {append ?? <Icon name="keyboard_arrow_down" size={20} />}
        </button>
      </Popover>

      {!disabled && hasError && <Errors id={`${id}-errors`} errors={errors} />}
    </div>
  );
};
